import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Globe, Play, ShieldCheck, Database, Loader2, Check, RefreshCw, Clock, Search, Users, UserCheck, UserX, Trash2, User, LogOut, MapPin, Download } from 'lucide-react';
import { Layout } from './Layout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';

export const SettingsPage = () => {
  const AHREFS_DISCOVER_ENABLED = false;
  const { user, isAdmin, logout, updateUser } = useAuth();
  const [settingsTab, setSettingsTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allGeos, setAllGeos] = useState([]);
  const [activeGeos, setActiveGeos] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [runningAction, setRunningAction] = useState(null);
  const [dfLogin, setDfLogin] = useState('');
  const [dfPassword, setDfPassword] = useState('');
  const [dfStatus, setDfStatus] = useState(null);
  const [dfTesting, setDfTesting] = useState(false);
  const [openaiCheckEnabled, setOpenaiCheckEnabled] = useState(false);
  const [discoveryLog, setDiscoveryLog] = useState(null);
  const [clientHasAhrefs, setClientHasAhrefs] = useState(false);
  const [clientHasDfs, setClientHasDfs] = useState(false);
  const [cleanupCandidates, setCleanupCandidates] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupSelection, setCleanupSelection] = useState({});

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, statusRes, schedRes, keysRes] = await Promise.all([
        apiService.getSettings(),
        apiService.getPipelineStatus().catch(() => null),
        apiService.getSchedule().catch(() => null),
        !isAdmin ? apiService.getMyApiKeys().catch(() => null) : Promise.resolve(null),
      ]);
      setAllGeos(settingsRes.data.all_geos || []);
      setActiveGeos(settingsRes.data.active_geos || []);
      if (keysRes?.data?.ahrefs && keysRes.data.ahrefs !== 'NOT SET') {
        setClientHasAhrefs(true);
      }
      if (keysRes?.data?.dataforseo_login && keysRes.data.dataforseo_login !== 'NOT SET' && keysRes.data.dataforseo_password_set) {
        setClientHasDfs(true);
      }
      if (schedRes?.data?.jobs) {
        setSchedule(schedRes.data);
      } else {
        setSchedule(settingsRes.data.pipeline_schedule || null);
      }
      if (settingsRes.data.dataforseo) {
        setDfLogin(settingsRes.data.dataforseo.login || '');
        setDfStatus(settingsRes.data.dataforseo.password_set ? 'configured' : null);
      }
      if (settingsRes.data.openai_brand_check) {
        setOpenaiCheckEnabled(!!settingsRes.data.openai_brand_check.enabled);
      }
      if (statusRes) setPipelineStatus(statusRes.data);
      if (isAdmin) {
        await loadCleanupCandidates();
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const timer = setInterval(async () => {
      try {
        const statusRes = await apiService.getPipelineStatus();
        if (statusRes?.data) setPipelineStatus(statusRes.data);
      } catch {
        // keep last known status
      }
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const loadCleanupCandidates = async () => {
    try {
      setCleanupLoading(true);
      const res = await apiService.getUnconfirmedCleanupCandidates('pending_review', 300);
      setCleanupCandidates(res.data?.candidates || []);
    } catch (err) {
      console.error('Error loading cleanup candidates:', err);
    } finally {
      setCleanupLoading(false);
    }
  };

  const toggleGeo = (code) => {
    setActiveGeos(prev =>
      prev.includes(code) ? prev.filter(g => g !== code) : [...prev, code]
    );
  };

  const selectRegion = (region) => {
    const regionCodes = allGeos.filter(g => g.region === region).map(g => g.code);
    const allActive = regionCodes.every(c => activeGeos.includes(c));
    if (allActive) {
      setActiveGeos(prev => prev.filter(g => !regionCodes.includes(g)));
    } else {
      setActiveGeos(prev => [...new Set([...prev, ...regionCodes])]);
    }
  };

  const handleSaveGeos = async () => {
    setSaving(true);
    setMessage('');
    try {
      await apiService.updateSettings({ active_geos: activeGeos });
      setMessage('GEO settings saved!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePipelineAction = async (action, label) => {
    setRunningAction(action);
    setMessage('');
    try {
      let res;
      switch (action) {
        case 'run': res = await apiService.runPipeline(); break;
        case 'daily-quality': res = await apiService.runDailyQualityRun(); break;
        case 'pause-jobs': res = await apiService.pausePipeline(); break;
        case 'resume-jobs': res = await apiService.resumePipeline(); break;
        case 'stop-jobs': res = await apiService.stopPipeline(); break;
        case 'reset-jobs': res = await apiService.resetPipeline(); break;
        case 'backfill': res = await apiService.runBackfill(14); break;
        case 'spikes': res = await apiService.runDetectSpikes(); break;
        case 'reprocess': res = await apiService.runReprocess(); break;
        case 'license-check': res = await apiService.runLicenseCheck(); break;
        case 'unconfirmed-sync': res = await apiService.runUnconfirmedCleanupSync(); break;
        case 'unconfirmed-apply': res = await apiService.applyApprovedCleanupDeletions(false, 500, true); break;
        case 'unconfirmed-scan-delete-now': res = await apiService.runUnconfirmedScanDeleteNow(5000, 500, false); break;
        case 'classify-all': res = await apiService.classifyAll(false); break;
        case 'verify-all': res = await apiService.verifyAll(); break;
        case 'verify-ai-fallback': res = await apiService.runAiFallbackVerify(5000, false, true, true, true, true); break;
        case 'serp-check-all': res = await apiService.serpCheckAll(); break;
        case 'ahrefs-cross-geo': res = await apiService.ahrefsCrossGeo({ max_units: 120 }); break;
        case 'ahrefs-discover':
          if (!AHREFS_DISCOVER_ENABLED) {
            setMessage('Ahrefs Discover disabled: use Cross-GEO only for local-noise validation.');
            return;
          }
          res = await apiService.ahrefsDiscover({ max_units: 1200, max_geos: 2 });
          break;
        case 'ahrefs-ingest': res = await apiService.ahrefsIngest(); break;
        case 'searchapi-collect': res = await apiService.searchapiCollect(); break;
        case 'review-signals': res = await apiService.runReviewSignals(800, 3, 3, true, false); break;
        case 'verify-domains': res = await apiService.verifyDomains(); break;
        case 'pbn-analyze-all': res = await apiService.pbnAnalyzeAll(); break;
        case 'serp-segments': res = await apiService.runSerpSegments(); break;
        case 'serp-segments-incr': res = await apiService.runSerpSegmentsIncremental(); break;
        default: return;
      }
      if (action === 'unconfirmed-apply') {
        const stats = res?.data?.stats || {};
        setMessage(`Deleted: ${stats.deleted_entities || 0} | Considered: ${stats.considered || 0} | Skipped: ${(stats.skipped_protected || 0) + (stats.skipped_evidence || 0) + (stats.skipped_missing || 0)}`);
      } else if (action === 'unconfirmed-scan-delete-now') {
        const sync = res?.data?.sync || {};
        const apply = res?.data?.apply || {};
        setMessage(
          `Scan+Delete done | Scanned: ${sync.scanned_max || 0} | Queue created: ${sync.created || 0} | Updated: ${sync.updated || 0} | Deleted: ${apply.deleted_entities || 0} | Skipped: ${(apply.skipped_protected || 0) + (apply.skipped_evidence || 0) + (apply.skipped_missing || 0)}`
        );
      } else if (action === 'pause-jobs' || action === 'resume-jobs' || action === 'stop-jobs' || action === 'reset-jobs') {
        setMessage(res?.data?.message || `${label} done`);
      } else if (action === 'verify-ai-fallback') {
        const stats = res?.data?.stats || {};
        if (Object.keys(stats).length > 0) {
          setMessage(
            `GPT checked: ${stats.checked || 0} | Keep by AI: ${stats.kept_by_ai || 0} | Promoted (Casino? -> Verified): ${stats.promoted_from_likely_to_verified || 0} | Updated: ${stats.updated || 0} | Likely: ${stats.likely_casino || 0} | Not gambling: ${stats.not_gambling || 0} | Noise blacklisted: ${stats.blacklisted_noise || 0} | Skipped(local signals): ${stats.skipped_has_local_signals || 0}`
          );
        } else {
          setMessage(res?.data?.message || 'AI fallback started in background');
        }
      } else if (action === 'ahrefs-cross-geo' || action === 'ahrefs-discover') {
        const p = res?.data?.params || {};
        setMessage(
          `${res?.data?.message || `${label} started`} | units left: ${p.units_left ?? 'n/a'} | max_units: ${p.effective_max_units ?? p.requested_max_units ?? 'n/a'}${action === 'ahrefs-discover' ? ` | max_geos: ${p.effective_max_geos ?? p.requested_max_geos ?? 'n/a'}` : ''}`
        );
      } else {
        setMessage(res.data.message || `${label} started!`);
      }
      // Capture discovery log if available
      if (action === 'ahrefs-discover' && res.data.log) {
        setDiscoveryLog(res.data);
      }
      if (action === 'unconfirmed-sync' || action === 'unconfirmed-apply' || action === 'unconfirmed-scan-delete-now') {
        await loadCleanupCandidates();
        await loadSettings();
      }
      setTimeout(() => setMessage(''), 15000);
    } catch (err) {
      const detail = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : (err.response?.data?.message || (typeof err.message === 'string' ? err.message : JSON.stringify(err.response?.data || err.message)));
      setMessage(`Error: ${detail === 'pipeline_paused' ? 'Pipeline is paused. Resume it to start new jobs.' : detail}`);
    } finally {
      setRunningAction(null);
    }
  };

  const regions = [...new Set(allGeos.map(g => g.region))];
  const visibleCleanupIds = cleanupCandidates.map(c => c.entity_id).filter(Boolean);
  const selectedCleanupIds = Object.entries(cleanupSelection).filter(([, v]) => v).map(([id]) => id);
  const selectedVisibleCount = visibleCleanupIds.filter(id => cleanupSelection[id]).length;
  const allVisibleSelected = visibleCleanupIds.length > 0 && selectedVisibleCount === visibleCleanupIds.length;

  const toggleSelectAllVisibleCleanup = () => {
    setCleanupSelection(prev => {
      const next = { ...prev };
      if (allVisibleSelected) {
        visibleCleanupIds.forEach(id => { delete next[id]; });
      } else {
        visibleCleanupIds.forEach(id => { next[id] = true; });
      }
      return next;
    });
  };

  const applyCleanupDecision = async (decision) => {
    if (selectedCleanupIds.length === 0) return;
    setRunningAction(`unconfirmed-${decision}`);
    setMessage('');
    try {
      const res = await apiService.decideUnconfirmedCleanupCandidates(selectedCleanupIds, decision);
      setMessage(`Updated: ${res.data.updated} candidates`);
      setCleanupSelection({});
      await loadCleanupCandidates();
    } catch (err) {
      setMessage(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRunningAction(null);
    }
  };

  const deleteSelectedCleanupNow = async () => {
    if (selectedCleanupIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedCleanupIds.length} selected candidates now?`)) return;
    setRunningAction('unconfirmed-delete-selected');
    setMessage('');
    try {
      await apiService.decideUnconfirmedCleanupCandidates(selectedCleanupIds, 'approved_delete');
      const res = await apiService.applyApprovedCleanupDeletions(false, 2000, true);
      const stats = res?.data?.stats || {};
      setMessage(`Deleted: ${stats.deleted_entities || 0} | Skipped (protected/evidence): ${(stats.skipped_protected || 0) + (stats.skipped_evidence || 0)}`);
      setCleanupSelection({});
      await loadCleanupCandidates();
    } catch (err) {
      setMessage(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRunningAction(null);
    }
  };

  if (loading) {
    return (
      <Layout activeTab="settings">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeTab="settings">
      <div className="mb-6">
        <h2 className="text-2xl font-display font-bold text-foreground flex items-center gap-2">
          <Settings className="w-6 h-6 text-primary" />
          Settings
        </h2>
        <p className="text-sm text-muted-foreground mb-4">Configure data collection regions and pipeline</p>
        
        {/* Tabs */}
        <div className="flex gap-1 border-b border-border">
          {[
            { id: 'general', label: 'General', icon: Settings },
            ...(isAdmin ? [{ id: 'users', label: 'Users', icon: Users }] : []),
            { id: 'profile', label: 'Profile', icon: User },
          ].map(tab => (
            <button key={tab.id} onClick={() => setSettingsTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                settingsTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {settingsTab === 'general' && (<div>

      {message && (
        <div className={`mb-4 p-3 rounded-md text-sm ${message.includes('Error') ? 'bg-destructive/10 text-destructive' : 'bg-green-50 text-green-700'}`}>
          {message}
        </div>
      )}

      {/* GEO Selection - Admin only */}
      {isAdmin && (
      <Card className="card-elevated mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Active Regions ({activeGeos.length} selected)
            </CardTitle>
            {isAdmin && (
              <button
                onClick={handleSaveGeos}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {regions.map(region => {
            const regionGeos = allGeos.filter(g => g.region === region);
            const activeCount = regionGeos.filter(g => activeGeos.includes(g.code)).length;
            return (
              <div key={region} className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => isAdmin && selectRegion(region)}
                    className={`text-xs font-semibold uppercase tracking-wider ${isAdmin ? 'cursor-pointer hover:text-primary' : ''} text-muted-foreground`}
                  >
                    {region}
                  </button>
                  <span className="text-xs text-muted-foreground">({activeCount}/{regionGeos.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {regionGeos.map(geo => (
                    <button
                      key={geo.code}
                      onClick={() => isAdmin && toggleGeo(geo.code)}
                      disabled={!isAdmin}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        activeGeos.includes(geo.code)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card border border-border text-muted-foreground hover:border-primary/50'
                      } ${!isAdmin ? 'cursor-default' : 'cursor-pointer'}`}
                    >
                      {geo.code} - {geo.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
      )}

      {/* Pipeline Controls - Admin only */}
      {isAdmin && (
        <Card className="card-elevated mb-6">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Pipeline Controls
                {pipelineStatus?.control?.paused && (
                  <span className="text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-300">
                    Paused
                  </span>
                )}
              </CardTitle>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePipelineAction('pause-jobs', 'Pause')}
                  disabled={!!runningAction || pipelineStatus?.control?.paused}
                >
                  {runningAction === 'pause-jobs' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Pause
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePipelineAction('resume-jobs', 'Resume')}
                  disabled={!!runningAction || !pipelineStatus?.control?.paused}
                >
                  {runningAction === 'resume-jobs' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Resume
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePipelineAction('stop-jobs', 'Stop')}
                  disabled={!!runningAction}
                  className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                >
                  {runningAction === 'stop-jobs' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Stop
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePipelineAction('reset-jobs', 'Reset')}
                  disabled={!!runningAction}
                  className="text-red-700 border-red-300 hover:bg-red-50"
                >
                  {runningAction === 'reset-jobs' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Reset
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => handlePipelineAction('daily-quality', 'Daily Quality Checks')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-emerald-300 transition-colors disabled:opacity-50"
              >
                {runningAction === 'daily-quality' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5 text-emerald-700" />}
                <div className="text-left">
                  <p className="font-semibold text-sm">Daily Quality Checks</p>
                  <p className="text-xs text-muted-foreground">Spikes → Domains → License → Classify → Verify</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('run', 'Full Pipeline')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'run' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 text-green-700" />}
                <div className="text-left">
                  <p className="font-semibold text-sm">Run Full Pipeline</p>
                  <p className="text-xs text-muted-foreground">Collect → Extract → Score → Detect</p>
                </div>
              </button>

              <button
                onClick={() => handlePipelineAction('backfill', 'Backfill')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'backfill' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Database className="w-5 h-5 text-blue-700" />}
                <div className="text-left">
                  <p className="font-semibold text-sm">Backfill 14 Days</p>
                  <p className="text-xs text-muted-foreground">Generate historical baseline</p>
                </div>
              </button>

              <button
                onClick={() => handlePipelineAction('spikes', 'Spike Detection')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'spikes' ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5 text-yellow-700" />}
                <div className="text-left">
                  <p className="font-semibold text-sm">Detect Spikes</p>
                  <p className="text-xs text-muted-foreground">Run spike detection now</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('reprocess')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'reprocess' ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5 text-purple-700" />}
                <div className="text-left">
                  <p className="font-semibold text-sm">Reprocess</p>
                  <p className="text-xs text-muted-foreground">Re-extract from signals</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('license-check', 'License Check')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-yellow-300 transition-colors disabled:opacity-50"
              >
                {runningAction === 'license-check' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg">L</span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">License Check</p>
                  <p className="text-xs text-muted-foreground">Scan UKGC + static DB</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('serp-segments', 'SERP Segments Baseline')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-cyan-500/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'serp-segments' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg">S</span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">SERP Segments</p>
                  <p className="text-xs text-muted-foreground">Blog/Forum/Social/Grey (full)</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('serp-segments-incr', 'SERP Segments Incremental')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-cyan-500/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'serp-segments-incr' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg">Si</span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">SERP Seg. Incr.</p>
                  <p className="text-xs text-muted-foreground">Watch/hot brands only</p>
                </div>
              </button>
            </div>
            <div className="text-xs text-muted-foreground mb-4">
              Recommended daily order: 1) Detect spikes, 2) Verify domains, 3) License check, 4) Classify brands, 5) Verify all, 6) Sync trash/unconfirmed review queue.
            </div>
            
            <h4 className="text-sm font-semibold text-foreground mb-3 mt-4 flex items-center gap-2">
              <span></span> Legal Verification Module
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Cross-checks license + SERP + Ahrefs + name patterns. Requires 2+ signals to verify.</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <button
                onClick={() => handlePipelineAction('classify-all', 'Classify Brands')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'classify-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Classify Brands</p>
                  <p className="text-xs text-muted-foreground">Online / Offline / Not Gambling</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('verify-all', 'Verify All')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'verify-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Verify All</p>
                  <p className="text-xs text-muted-foreground">Cross-check licenses + SERP + domains</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('verify-ai-fallback', 'Check all via GPT')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-violet-500/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'verify-ai-fallback' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Check all via GPT</p>
                  <p className="text-xs text-muted-foreground">Only no-signal brands</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('serp-check-all', 'SERP Check All')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'serp-check-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">SERP Check All</p>
                  <p className="text-xs text-muted-foreground">Fetch SERP for entities without snapshots</p>
                </div>
              </button>
            </div>

            <h4 className="text-sm font-semibold text-foreground mb-3 mt-4 flex items-center gap-2">
              <span></span> Ahrefs Brand Discovery
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Ahrefs is used mainly for Cross-GEO validation (local noise vs scalable demand). Discover is disabled by default.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <button
                onClick={() => handlePipelineAction('ahrefs-cross-geo', 'Cross-GEO Expansion')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'ahrefs-cross-geo' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Cross-GEO Spikes</p>
                  <p className="text-xs text-muted-foreground">Check spike brands in other geos</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('ahrefs-discover', 'Brand Discovery')}
                disabled={runningAction || !AHREFS_DISCOVER_ENABLED}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'ahrefs-discover' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Discover Brands (Disabled)</p>
                  <p className="text-xs text-muted-foreground">Enable only for manual deep discovery</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('ahrefs-ingest', 'Ingest Brands')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'ahrefs-ingest' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Ingest Discovered</p>
                  <p className="text-xs text-muted-foreground">Create entities from discoveries</p>
                </div>
              </button>
            </div>

            {/* Discovery Log */}
            {discoveryLog && discoveryLog.log && (
              <div className="mb-6 p-4 bg-muted/30 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-foreground"> Discovery Log</h5>
                  <button onClick={() => setDiscoveryLog(null)} className="text-xs text-muted-foreground hover:text-foreground"> close</button>
                </div>
                {discoveryLog.stats && (
                  <div className="flex gap-4 mb-2 text-xs">
                    <span className="text-muted-foreground">GEOs: <span className="text-foreground font-mono">{discoveryLog.stats.geos_checked}</span></span>
                    <span className="text-muted-foreground">Keywords: <span className="text-foreground font-mono">{discoveryLog.stats.keywords_total}</span></span>
                    <span className="text-muted-foreground">Units: <span className="text-foreground font-mono">{discoveryLog.stats.units_used}</span></span>
                    <span className={`font-semibold ${discoveryLog.stats.new_brands > 0 ? 'text-green-700' : 'text-yellow-700'}`}>
                      New: {discoveryLog.stats.new_brands}
                    </span>
                  </div>
                )}
                <div className="max-h-80 overflow-y-auto font-mono text-xs space-y-0.5">
                  {discoveryLog.log.map((line, i) => (
                    <div key={i} className={`${line.includes(' NEW') ? 'text-green-700 font-semibold' : line.includes('') ? 'text-muted-foreground/60' : line.includes('') ? 'text-yellow-700' : line.includes('') ? 'text-red-700' : line.includes('') ? 'text-primary font-semibold' : 'text-foreground/80'}`}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4 className="text-sm font-semibold text-foreground mb-3 mt-4 flex items-center gap-2">
              <span></span> SearchAPI.io — Multi-Engine Signals
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Collect signals from Google Trends, Bing, Yahoo, Yandex, TikTok, Google Play, App Store. Uses SearchAPI.io key (~$4/1000 req).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => handlePipelineAction('searchapi-collect', 'SearchAPI Collect')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'searchapi-collect' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Collect All Signals</p>
                  <p className="text-xs text-muted-foreground">Trending, Bing, TikTok, Play, Apps, Yandex</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('review-signals', 'Review Signals Enrichment')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'review-signals' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Review Signals</p>
                  <p className="text-xs text-muted-foreground">Trustpilot, Reddit, review sites from SERP</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('verify-domains', 'Verify Domains')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'verify-domains' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Verify Domains</p>
                  <p className="text-xs text-muted-foreground">Multi-signal official domain check</p>
                </div>
              </button>
            </div>

            <h4 className="text-sm font-semibold text-foreground mb-3 mt-4 flex items-center gap-2">
              <span></span> PBN / Network Detection
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Detects monobrand saturation, PBN networks, affiliate launches. Uses SERP data + DNS/ASN analysis.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <button
                onClick={() => handlePipelineAction('pbn-analyze-all', 'PBN Analyze All')}
                disabled={runningAction}
                className="flex items-center justify-center gap-2 p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'pbn-analyze-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">PBN Analyze All</p>
                  <p className="text-xs text-muted-foreground">NPI + IP/ASN clusters + name patterns</p>
                </div>
              </button>
            </div>

            <h4 className="text-sm font-semibold text-foreground mb-3 mt-4 flex items-center gap-2">
              <span></span> Unconfirmed / Trash Review Queue
            </h4>
            <p className="text-xs text-muted-foreground mb-3">
              Daily job adds unique unconfirmed brands to review queue. Admin decides what to delete.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePipelineAction('unconfirmed-sync', 'Sync Unconfirmed Queue')}
                disabled={runningAction}
              >
                {runningAction === 'unconfirmed-sync' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Scan now
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => applyCleanupDecision('approved_delete')}
                disabled={runningAction || selectedCleanupIds.length === 0}
              >
                Approve selected ({selectedCleanupIds.length})
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => applyCleanupDecision('keep')}
                disabled={runningAction || selectedCleanupIds.length === 0}
              >
                Keep selected
              </Button>
              <Button
                size="sm"
                onClick={() => handlePipelineAction('unconfirmed-apply', 'Delete Approved')}
                disabled={runningAction}
                className="bg-destructive/80 hover:bg-destructive text-white"
              >
                {runningAction === 'unconfirmed-apply' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Delete approved
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  if (window.confirm('Run Scan + Delete now? This will auto-approve pending unconfirmed candidates and delete immediately (with evidence/protected safety checks).')) {
                    handlePipelineAction('unconfirmed-scan-delete-now', 'Scan + Delete now');
                  }
                }}
                disabled={runningAction}
                className="bg-red-700 hover:bg-red-800 text-white"
                title="One-click operational cleanup: scan queue and delete pending candidates now"
              >
                {runningAction === 'unconfirmed-scan-delete-now' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Scan + Delete now
              </Button>
              <Button
                size="sm"
                onClick={deleteSelectedCleanupNow}
                disabled={runningAction || selectedCleanupIds.length === 0}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                {runningAction === 'unconfirmed-delete-selected' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Delete selected ({selectedCleanupIds.length})
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={loadCleanupCandidates}
                disabled={cleanupLoading}
              >
                {cleanupLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Refresh list
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={toggleSelectAllVisibleCleanup}
                disabled={cleanupCandidates.length === 0}
              >
                {allVisibleSelected ? 'Clear selection' : 'Select all'}
              </Button>
            </div>
            <div className="border border-border rounded-lg overflow-hidden mb-6">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] text-muted-foreground font-medium bg-muted/30">
                <div className="col-span-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisibleCleanup}
                    aria-label="Select all visible candidates"
                  />
                  <span>Pick</span>
                </div>
                <div className="col-span-4">Brand</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-1 text-right">Seen</div>
                <div className="col-span-2">Review after</div>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {cleanupCandidates.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-muted-foreground">No pending candidates</div>
                ) : cleanupCandidates.map((c) => (
                  <div key={c.entity_id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-border/60 items-center">
                    <div className="col-span-1">
                      <input
                        type="checkbox"
                        checked={!!cleanupSelection[c.entity_id]}
                        onChange={(e) => setCleanupSelection(prev => ({ ...prev, [c.entity_id]: e.target.checked }))}
                      />
                    </div>
                    <div className="col-span-4 truncate text-foreground" title={c.canonical_name || c.entity_id}>
                      {c.canonical_name || c.entity_id}
                    </div>
                    <div className="col-span-2 text-muted-foreground">{c.verification_status || 'unverified'}</div>
                    <div className="col-span-2 text-muted-foreground">{c.entity_type || 'unknown'}</div>
                    <div className="col-span-1 text-right text-muted-foreground">{c.seen_count || 1}</div>
                    <div className="col-span-2 text-muted-foreground">
                      {c.review_after ? new Date(c.review_after).toLocaleDateString() : '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pipeline Status & Recent Jobs — visible to ALL users */}
      {pipelineStatus && (
        <Card className="card-elevated">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="w-5 h-5" /> Pipeline Status
              </CardTitle>
              <div className="text-xs text-muted-foreground">
                jobs: <span className="font-mono text-foreground">{pipelineStatus?.control?.running_count ?? 0}</span>
                {pipelineStatus?.control?.paused ? (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-300">paused</span>
                ) : (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-300">live</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
              {Object.entries(pipelineStatus.adapters || {}).map(([name, info]) => (
                <div key={name} className="p-2 bg-background rounded border border-border">
                  <p className="text-xs text-muted-foreground capitalize">{name.replace('_', ' ')}</p>
                  <p className={`font-medium ${info.mode === 'live' ? 'text-green-700' : 'text-yellow-700'}`}>
                    {info.mode?.toUpperCase()}
                  </p>
                </div>
              ))}
            </div>
            {pipelineStatus.recent_jobs?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-2">Recent Jobs</p>
                <div className="space-y-1">
                  {pipelineStatus.recent_jobs.slice(0, 10).map((job, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 bg-background rounded gap-2">
                      <span className="text-foreground font-medium min-w-[120px]">{job.name}</span>
                      {(job.message || job.detail) && (
                        <span
                          className="text-muted-foreground flex-1 truncate"
                          title={[job.message, job.detail].filter(Boolean).join(' | ')}
                        >
                          {job.message || job.detail}
                          {job.step && job.total_steps ? ` (${job.step}/${job.total_steps})` : ''}
                        </span>
                      )}
                      <span className={
                        (job.status === 'completed' || job.status === 'done') ? 'text-green-700' :
                        (job.status === 'running' && job.running_long) ? 'text-amber-700 font-bold animate-pulse' :
                        job.status === 'running' ? 'text-blue-700 font-bold animate-pulse' :
                        job.status === 'cancelled' ? 'text-yellow-700' :
                        (job.status === 'timed_out' || job.is_stale) ? 'text-gray-700' :
                        job.status === 'queued' ? 'text-yellow-700' : 'text-red-700'
                      }>
                        {job.status === 'running' && job.running_long ? 'running_long' : job.status}
                      </span>
                      <span className="text-muted-foreground min-w-[70px] text-right">
                        {job.status === 'running' && typeof job.running_for_sec === 'number'
                          ? `${Math.floor(job.running_for_sec / 60)}m`
                          : (job.started ? new Date(job.started).toLocaleTimeString() : '')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {isAdmin && (
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-700" />
            DataForSEO API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Login (email)</label>
              <input
                type="text"
                value={dfLogin}
                onChange={e => setDfLogin(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">API Password</label>
              <input
                type="password"
                value={dfPassword}
                onChange={e => setDfPassword(e.target.value)}
                placeholder={dfStatus === 'configured' ? '••••••••' : 'Enter API password'}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={async () => {
                try {
                  setSaving(true);
                  const payload = {};
                  if (dfLogin) payload.dataforseo_login = dfLogin;
                  if (dfPassword) payload.dataforseo_password = dfPassword;
                  await apiService.updateSettings(payload);
                  setMessage('DataForSEO credentials saved!');
                  setDfStatus('configured');
                  setTimeout(() => setMessage(''), 3000);
                } catch (err) {
                  setMessage('Failed to save');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              Save Credentials
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setDfTesting(true);
                try {
                  const res = await apiService.testDataForSEO();
                  if (res.data.ok) {
                    setDfStatus('connected');
                    setMessage(` Connected! Balance: $${res.data.balance?.toFixed(2)}`);
                  } else {
                    setDfStatus('error');
                    setMessage(` ${res.data.error}`);
                  }
                } catch (err) {
                  setDfStatus('error');
                  setMessage(' Connection failed');
                }
                setDfTesting(false);
                setTimeout(() => setMessage(''), 5000);
              }}
              disabled={dfTesting}
            >
              {dfTesting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Test Connection
            </Button>
            {dfStatus === 'connected' && <span className="text-xs text-emerald-700"> Connected</span>}
            {dfStatus === 'configured' && <span className="text-xs text-yellow-700"> Configured</span>}
            {dfStatus === 'error' && <span className="text-xs text-red-700"> Error</span>}
          </div>
          <p className="text-xs text-muted-foreground">
            Used in Cabinet → SERP Analysis. ~$0.002 per request.{' '}
            <a href="https://app.dataforseo.com" target="_blank" rel="noopener noreferrer" className="text-blue-700">
              Get API key →
            </a>
          </p>
        </CardContent>
      </Card>
      )}

      {isAdmin && (
      <Card className="card-elevated mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span></span> OpenAI Brand Fallback
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            If brand has no local verification signals, run short AI check: "is this an online casino or not".
          </p>
          <div className="flex items-center gap-3">
            <label className="text-sm text-foreground flex items-center gap-2">
              <input
                type="checkbox"
                checked={openaiCheckEnabled}
                onChange={(e) => setOpenaiCheckEnabled(e.target.checked)}
              />
              Enable OpenAI fallback check
            </label>
            <Button
              size="sm"
              onClick={async () => {
                try {
                  setSaving(true);
                  await apiService.updateSettings({ openai_brand_check_enabled: openaiCheckEnabled });
                  setMessage(`OpenAI fallback ${openaiCheckEnabled ? 'enabled' : 'disabled'}`);
                  setTimeout(() => setMessage(''), 3000);
                } catch (err) {
                  setMessage('Error: failed to update OpenAI fallback setting');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            API key is configured in Profile → API Keys → OpenAI API Key.
          </p>
        </CardContent>
      </Card>
      )}

      {/* Documentation - Client only */}
      {!isAdmin && (
        <Card className="card-elevated mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
               How to Use Entity Trend Radar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <div>
              <h4 className="text-foreground font-medium mb-1">1. Set Up API Keys</h4>
              <p>Go to <span className="text-primary">Settings → Profile</span> and add your DataForSEO, Ahrefs, and/or SE Ranking API keys. These are used for SERP analysis and brand discovery.</p>
            </div>
            <div>
              <h4 className="text-foreground font-medium mb-1">2. Dashboard</h4>
              <p>The main dashboard shows trending gambling brands across all tracked regions. Use the GEO filter to focus on specific markets. Click any brand to see detailed analytics.</p>
            </div>
            <div>
              <h4 className="text-foreground font-medium mb-1">3. Cabinet</h4>
              <p>Add up to <span className="text-primary font-medium">20 brands</span> to your Cabinet for ongoing monitoring. Track growth trends, spike events, and compare brands side-by-side.</p>
            </div>
            <div>
              <h4 className="text-foreground font-medium mb-1">4. Brand Detail</h4>
              <p>Click on any brand to see signal breakdown (Google Suggest, YouTube, Reddit, News), historical trends, and SERP analysis with competitor mapping.</p>
            </div>
            <div>
              <h4 className="text-foreground font-medium mb-1">5. Budget</h4>
              <p>Monitor your API usage and remaining limits in the <span className="text-primary">Budget</span> tab. Balances update in real-time from your connected services.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isAdmin && clientHasAhrefs && (
        <Card className="card-elevated mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span></span> Ahrefs Brand Discovery
            </CardTitle>
            <p className="text-xs text-muted-foreground">Discover new brands via local keywords. Expand spike brands to other geos. ~1-5 units per check.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <button
                onClick={() => handlePipelineAction('ahrefs-cross-geo', 'Cross-GEO Expansion')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'ahrefs-cross-geo' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Cross-GEO Spikes</p>
                  <p className="text-xs text-muted-foreground">Check spike brands in other geos</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('ahrefs-discover', 'Brand Discovery')}
                disabled={runningAction || !AHREFS_DISCOVER_ENABLED}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'ahrefs-discover' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Discover Brands (Disabled)</p>
                  <p className="text-xs text-muted-foreground">Enable only for manual deep discovery</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('ahrefs-ingest', 'Ingest Brands')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'ahrefs-ingest' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Ingest Discovered</p>
                  <p className="text-xs text-muted-foreground">Create entities from discoveries</p>
                </div>
              </button>
            </div>

            {/* Discovery Log */}
            {discoveryLog && discoveryLog.log && (
              <div className="mb-6 p-4 bg-muted/30 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-foreground"> Discovery Log</h5>
                  <button onClick={() => setDiscoveryLog(null)} className="text-xs text-muted-foreground hover:text-foreground"> close</button>
                </div>
                {discoveryLog.stats && (
                  <div className="flex gap-4 mb-2 text-xs">
                    <span className="text-muted-foreground">GEOs: <span className="text-foreground font-mono">{discoveryLog.stats.geos_checked}</span></span>
                    <span className="text-muted-foreground">Keywords: <span className="text-foreground font-mono">{discoveryLog.stats.keywords_total}</span></span>
                    <span className="text-muted-foreground">Units: <span className="text-foreground font-mono">{discoveryLog.stats.units_used}</span></span>
                    <span className={`font-semibold ${discoveryLog.stats.new_brands > 0 ? 'text-green-700' : 'text-yellow-700'}`}>
                      New: {discoveryLog.stats.new_brands}
                    </span>
                  </div>
                )}
                <div className="max-h-80 overflow-y-auto font-mono text-xs space-y-0.5">
                  {discoveryLog.log.map((line, i) => (
                    <div key={i} className={`${line.includes(' NEW') ? 'text-green-700 font-semibold' : line.includes('') ? 'text-muted-foreground/60' : line.includes('') ? 'text-yellow-700' : line.includes('') ? 'text-red-700' : line.includes('') ? 'text-primary font-semibold' : 'text-foreground/80'}`}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4 className="text-sm font-semibold text-foreground mb-2 mt-2 flex items-center gap-2">
              <span></span> SearchAPI.io — Multi-Engine Signals
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Collect signals from Google Trends, Bing, Yahoo, Yandex, TikTok, Google Play, App Store.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => handlePipelineAction('searchapi-collect', 'SearchAPI Collect')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'searchapi-collect' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Collect All Signals</p>
                  <p className="text-xs text-muted-foreground">Trending, Bing, TikTok, Play, Apps, Yandex</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('review-signals', 'Review Signals Enrichment')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'review-signals' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Review Signals</p>
                  <p className="text-xs text-muted-foreground">Trustpilot, Reddit, review sites</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('verify-domains', 'Verify Domains')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'verify-domains' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Verify Domains</p>
                  <p className="text-xs text-muted-foreground">Multi-signal official domain check</p>
                </div>
              </button>
            </div>

            <h4 className="text-sm font-semibold text-foreground mb-2 mt-2 flex items-center gap-2">
              <span></span> PBN / Network Detection
            </h4>
            <p className="text-xs text-muted-foreground mb-3">Detects monobrand saturation, PBN networks, affiliate launches. Uses SERP data + DNS/ASN analysis.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => handlePipelineAction('pbn-analyze-all', 'PBN Analyze All')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'pbn-analyze-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">PBN Analyze All</p>
                  <p className="text-xs text-muted-foreground">NPI + IP/ASN clusters + name patterns</p>
                </div>
              </button>
            </div>

            {message && (
              <div className={`mt-3 p-2 rounded text-sm ${message.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                {message}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!isAdmin && clientHasDfs && (
        <Card className="card-elevated mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span></span> Legal Verification Module
            </CardTitle>
            <p className="text-xs text-muted-foreground">Cross-checks license + SERP + Ahrefs + name patterns. Requires 2+ signals to verify.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => handlePipelineAction('classify-all', 'Classify Brands')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'classify-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Classify Brands</p>
                  <p className="text-xs text-muted-foreground">Online / Offline / Not Gambling</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('verify-all', 'Verify All')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'verify-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">Verify All</p>
                  <p className="text-xs text-muted-foreground">Cross-check licenses + SERP + domains</p>
                </div>
              </button>
              <button
                onClick={() => handlePipelineAction('serp-check-all', 'SERP Check All')}
                disabled={runningAction}
                className="flex items-center gap-2 p-3 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors disabled:opacity-50"
              >
                {runningAction === 'serp-check-all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-lg"></span>}
                <div className="text-left">
                  <p className="font-semibold text-sm">SERP Check All</p>
                  <p className="text-xs text-muted-foreground">Fetch SERP for entities without snapshots</p>
                </div>
              </button>
            </div>
            {message && !clientHasAhrefs && (
              <div className={`mt-3 p-2 rounded text-sm ${message.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                {message}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {schedule && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Automatic Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            {schedule.jobs ? (
              <div className="space-y-3">
                {schedule.jobs.map(job => {
                  const now = new Date();
                  const nextRun = job.next_run ? new Date(job.next_run) : null;
                  const lastRun = job.last_run ? new Date(job.last_run) : null;

                  const timeUntil = (target) => {
                    if (!target) return null;
                    const diff = target - now;
                    if (diff < 0) return 'overdue';
                    const h = Math.floor(diff / 3600000);
                    const m = Math.floor((diff % 3600000) / 60000);
                    if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
                    if (h > 0) return `${h}h ${m}m`;
                    return `${m}m`;
                  };

                  const timeSince = (target) => {
                    if (!target) return null;
                    const diff = now - target;
                    if (diff < 0) return 'just now';
                    const h = Math.floor(diff / 3600000);
                    const m = Math.floor((diff % 3600000) / 60000);
                    if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h ago`;
                    if (h > 0) return `${h}h ${m}m ago`;
                    if (m > 0) return `${m}m ago`;
                    return 'just now';
                  };

                  const fmt = (d) => d ? d.toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', timeZone:'UTC' }) + ' UTC' : '—';

                  return (
                    <div key={job.id} className="p-3 bg-background rounded border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-foreground capitalize">{job.id.replace(/_/g, ' ')}</span>
                        {job.last_status && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${job.last_status === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                            {job.last_status === 'ok' ? ' OK' : ' Error'}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div>
                          <span className="text-muted-foreground">Last run: </span>
                          <span className="text-foreground">{lastRun ? fmt(lastRun) : 'Never'}</span>
                          {lastRun && <span className="text-muted-foreground ml-1">({timeSince(lastRun)})</span>}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Next run: </span>
                          <span className="text-foreground">{nextRun ? fmt(nextRun) : '—'}</span>
                          {nextRun && <span className="text-primary ml-1 font-medium">in {timeUntil(nextRun)}</span>}
                        </div>
                      </div>
                      {job.last_details && (
                        <p className="text-xs text-muted-foreground mt-1 truncate" title={job.last_details}>
                          {job.last_details}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(schedule).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-background rounded border border-border">
                    <span className="text-sm text-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm text-muted-foreground">{value}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      </div>)}

      {settingsTab === 'users' && isAdmin && <UsersTab />}
      {settingsTab === 'profile' && <ProfileTab />}
    </Layout>
  );
};

// ── Users Management (Admin) ──
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [usersTab, setUsersTab] = useState('users'); // users | activity | runs | errors | devices
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [activityDays, setActivityDays] = useState(14);
  const [activityStatus, setActivityStatus] = useState('all');
  const [activitySearch, setActivitySearch] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionAction, setSessionAction] = useState(null);

  useEffect(() => { loadUsers(); }, []);
  useEffect(() => {
    if (usersTab === 'devices') loadAdminSessions();
    else if (usersTab !== 'users') loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersTab]);

  const loadUsers = async () => {
    try {
      const { data } = await apiService.getUsers();
      setUsers(data.users || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAction = async (email, action) => {
    setActionLoading(`${email}-${action}`);
    try {
      if (action === 'approve') await apiService.approveUser(email);
      else if (action === 'disable') await apiService.disableUser(email);
      else if (action === 'delete') {
        if (!window.confirm(`Delete ${email} permanently?`)) return;
        await apiService.deleteUser(email);
      }
      await loadUsers();
    } catch (e) { console.error(e); }
    setActionLoading(null);
  };

  const loadActivity = async () => {
    try {
      setActivityLoading(true);
      const category = usersTab === 'users' ? 'all' : usersTab;
      const { data } = await apiService.getAdminActivity({
        days: activityDays,
        limit: 400,
        user_email: selectedUserEmail || undefined,
        category,
        status: activityStatus,
        q: activitySearch || undefined,
      });
      setActivityItems(data?.items || []);
    } catch (e) {
      console.error('Failed to load admin activity', e);
      setActivityItems([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const exportActivityCsv = () => {
    const headers = ['timestamp', 'category', 'action', 'status', 'actor', 'actor_role', 'user_email', 'target', 'source', 'message'];
    const escape = (v) => {
      const s = String(v ?? '');
      if (s.includes('"') || s.includes(',') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(',')];
    activityItems.forEach((row) => {
      lines.push(headers.map((h) => escape(row?.[h] ?? '')).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin_activity_${usersTab}_${selectedUserEmail || 'all'}_${activityDays}d.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const loadAdminSessions = async () => {
    try {
      setSessionsLoading(true);
      const { data } = await apiService.getAdminSessions();
      setSessions(data?.items || []);
    } catch (e) {
      console.error('Failed to load admin sessions', e);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const revokeSession = async (sessionId) => {
    try {
      setSessionAction(sessionId);
      await apiService.revokeAdminSession(sessionId);
      await loadAdminSessions();
      await loadActivity();
    } catch (e) {
      console.error('Failed to revoke session', e);
    } finally {
      setSessionAction(null);
    }
  };

  if (loading) return <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mt-8" />;

  const pending = users.filter(u => u.status === 'pending');
  const active = users.filter(u => u.status === 'active');
  const disabled = users.filter(u => u.status === 'disabled');

  const UserRow = ({ u }) => (
    <div className="flex items-center justify-between p-3 bg-background rounded border border-border">
      <div className="flex items-center gap-3">
        {u.picture ? (
          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <User className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-foreground">{u.name || u.email}</p>
          <p className="text-xs text-muted-foreground">{u.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setSelectedUserEmail(u.email);
            setUsersTab('activity');
            setTimeout(() => loadActivity(), 0);
          }}
          className="text-xs px-2 py-1 h-auto"
        >
          View activity
        </Button>
        {u.allowed_geos?.length > 0 && (
          <span className="text-xs text-muted-foreground">{u.allowed_geos.length} GEOs</span>
        )}
        {u.status === 'pending' && (
          <Button size="sm" onClick={() => handleAction(u.email, 'approve')}
            disabled={actionLoading === `${u.email}-approve`}
            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-auto">
            {actionLoading === `${u.email}-approve` ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
            <span className="ml-1">Approve</span>
          </Button>
        )}
        {u.status === 'active' && (
          <Button size="sm" variant="outline" onClick={() => handleAction(u.email, 'disable')}
            disabled={actionLoading === `${u.email}-disable`}
            className="text-xs px-2 py-1 h-auto">
            <UserX className="w-3 h-3" />
          </Button>
        )}
        {u.status === 'disabled' && (
          <Button size="sm" onClick={() => handleAction(u.email, 'approve')}
            disabled={actionLoading === `${u.email}-approve`}
            className="text-xs px-2 py-1 h-auto">
            <UserCheck className="w-3 h-3" />
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => handleAction(u.email, 'delete')}
          disabled={actionLoading === `${u.email}-delete`}
          className="text-xs px-2 py-1 h-auto text-destructive hover:text-destructive">
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2">
        <div className="flex gap-1">
          {[
            { id: 'users', label: `Users (${users.length})` },
            { id: 'activity', label: 'Activity' },
            { id: 'runs', label: 'Runs' },
            { id: 'errors', label: 'Errors' },
            { id: 'devices', label: 'Devices' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setUsersTab(t.id)}
              className={`h-8 px-3 rounded-md text-xs border ${
                usersTab === t.id
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {usersTab !== 'users' && usersTab !== 'devices' && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={loadActivity} disabled={activityLoading}>
              {activityLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={exportActivityCsv} disabled={activityItems.length === 0}>
              <Download className="w-3.5 h-3.5 mr-1" />
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {usersTab === 'users' && (
        <>
          {pending.length > 0 && (
            <Card className="card-elevated border-yellow-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-700">
                  <Clock className="w-5 h-5" />
                  Pending Approval ({pending.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {pending.map(u => <UserRow key={u.email} u={u} />)}
              </CardContent>
            </Card>
          )}

          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-green-700" />
                Active Users ({active.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {active.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active users yet</p>
              ) : active.map(u => <UserRow key={u.email} u={u} />)}
            </CardContent>
          </Card>

          {disabled.length > 0 && (
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-muted-foreground">
                  <UserX className="w-5 h-5" />
                  Disabled ({disabled.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {disabled.map(u => <UserRow key={u.email} u={u} />)}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {usersTab === 'devices' && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Admin Logged Devices / Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-muted-foreground">
                Revoke unknown sessions to force re-login on that device.
              </p>
              <Button size="sm" variant="outline" onClick={loadAdminSessions} disabled={sessionsLoading}>
                {sessionsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Refresh
              </Button>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] text-muted-foreground font-medium bg-muted/30">
                <div className="col-span-2">IP</div>
                <div className="col-span-4">User-Agent</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-2">Last seen</div>
                <div className="col-span-1">Current</div>
                <div className="col-span-1"></div>
              </div>
              <div className="max-h-[480px] overflow-y-auto">
                {sessionsLoading ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading sessions...
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No active sessions found.</div>
                ) : sessions.map((s) => (
                  <div key={s.session_id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-border/60 items-start">
                    <div className="col-span-2 text-foreground">{s.ip || '—'}</div>
                    <div className="col-span-4 text-muted-foreground truncate" title={s.user_agent || ''}>{s.user_agent || '—'}</div>
                    <div className="col-span-2 text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</div>
                    <div className="col-span-2 text-muted-foreground">{s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : '—'}</div>
                    <div className="col-span-1 text-foreground">{s.is_current ? 'yes' : 'no'}</div>
                    <div className="col-span-1 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={s.is_current || sessionAction === s.session_id}
                        onClick={() => revokeSession(s.session_id)}
                        className="h-7 px-2 text-[11px]"
                        title={s.is_current ? 'Current session cannot be revoked from this device' : 'Revoke session'}
                      >
                        {sessionAction === s.session_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Revoke'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {usersTab !== 'users' && usersTab !== 'devices' && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-primary" />
              Admin Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground font-medium">User email</span>
                <select
                  value={selectedUserEmail}
                  onChange={(e) => setSelectedUserEmail(e.target.value)}
                  className="h-8 px-2 rounded-md bg-background border border-border text-xs"
                >
                  <option value="">All users</option>
                  {users.map((u) => (
                    <option key={u.email} value={u.email}>{u.email}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground font-medium">Period</span>
                <select
                  value={activityDays}
                  onChange={(e) => setActivityDays(Number(e.target.value))}
                  className="h-8 px-2 rounded-md bg-background border border-border text-xs"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground font-medium">Status</span>
                <select
                  value={activityStatus}
                  onChange={(e) => setActivityStatus(e.target.value)}
                  className="h-8 px-2 rounded-md bg-background border border-border text-xs"
                >
                  <option value="all">All</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                  <option value="running">Running</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground font-medium">Search</span>
                <input
                  value={activitySearch}
                  onChange={(e) => setActivitySearch(e.target.value)}
                  placeholder="email / job / action"
                  className="h-8 px-2 rounded-md bg-background border border-border text-xs"
                />
              </label>
            </div>
            <div className="mb-3">
              <Button size="sm" onClick={loadActivity} disabled={activityLoading}>
                {activityLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Apply filters
              </Button>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] text-muted-foreground font-medium bg-muted/30">
                <div className="col-span-2">Time</div>
                <div className="col-span-1">Type</div>
                <div className="col-span-2">Action</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Actor</div>
                <div className="col-span-2">User</div>
                <div className="col-span-2">Message</div>
              </div>
              <div className="max-h-[520px] overflow-y-auto">
                {activityLoading ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading logs...
                  </div>
                ) : activityItems.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No log records for selected filters.</div>
                ) : activityItems.map((row, idx) => (
                  <div key={`${row.timestamp}-${idx}`} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-border/60 items-start">
                    <div className="col-span-2 text-muted-foreground whitespace-nowrap">
                      {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
                    </div>
                    <div className="col-span-1 text-foreground">{row.category || '—'}</div>
                    <div className="col-span-2 text-foreground truncate" title={row.action || ''}>{row.action || '—'}</div>
                    <div className={`col-span-1 ${row.status === 'error' ? 'text-red-700' : row.status === 'running' ? 'text-blue-700' : 'text-emerald-700'}`}>
                      {row.status || '—'}
                    </div>
                    <div className="col-span-2 text-muted-foreground truncate" title={row.actor || ''}>{row.actor || '—'}</div>
                    <div className="col-span-2 text-muted-foreground truncate" title={row.user_email || ''}>{row.user_email || '—'}</div>
                    <div className="col-span-2 text-muted-foreground truncate" title={row.message || ''}>{row.message || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Profile Tab ──
const ProfileTab = () => {
  const { user, logout, updateUser } = useAuth();
  const [geos, setGeos] = useState(user?.allowed_geos || []);
  const [allGeos, setAllGeos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // API Keys
  const [apiKeys, setApiKeys] = useState({ dataforseo_login: '', dataforseo_password: '', ahrefs: '', seranking: '', searchapi: '', openai: '' });
  const [maskedKeys, setMaskedKeys] = useState({});
  const [showPassword, setShowPassword] = useState({});
  const [savingKeys, setSavingKeys] = useState(false);
  const [keyMessage, setKeyMessage] = useState('');
  const [budget, setBudget] = useState(null);
  const [trackedFilters, setTrackedFilters] = useState([]);
  const [trackedLoading, setTrackedLoading] = useState(false);
  const [trackedMsg, setTrackedMsg] = useState('');
  const [trackedBusyId, setTrackedBusyId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFilter, setHistoryFilter] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionItems, setSessionItems] = useState([]);
  const [sessionCap, setSessionCap] = useState({ count: 0, max_sessions: 5, remaining_slots: 0 });
  const [sessionAction, setSessionAction] = useState('');
  const [sessionMessage, setSessionMessage] = useState('');

  useEffect(() => {
    apiService.getSettings().then(({ data }) => {
      if (data?.all_geos) setAllGeos(data.all_geos.map(g => g.code || g));
    }).catch(() => {});
    // Load API keys
    apiService.getMyApiKeys().then(({ data }) => setMaskedKeys(data)).catch(() => {});
    // Load budget
    apiService.getMyBudget().then(({ data }) => setBudget(data)).catch(() => {});
    loadTrackedFilters();
    if (user?.role !== 'admin') {
      loadMySessions();
    }
  }, [user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMySessions = async () => {
    try {
      setSessionLoading(true);
      const { data } = await apiService.getMySessions();
      setSessionItems(data?.items || []);
      setSessionCap({
        count: Number(data?.count || 0),
        max_sessions: Number(data?.max_sessions || 5),
        remaining_slots: Number(data?.remaining_slots || 0),
      });
    } catch (e) {
      setSessionItems([]);
      setSessionMessage('Failed to load devices');
    } finally {
      setSessionLoading(false);
    }
  };

  const revokeMySession = async (sid) => {
    if (!sid) return;
    try {
      setSessionAction(sid);
      setSessionMessage('');
      await apiService.revokeMySession(sid);
      setSessionMessage('Device disconnected');
      await loadMySessions();
    } catch (e) {
      setSessionMessage(e?.response?.data?.detail || 'Failed to disconnect device');
    } finally {
      setSessionAction('');
      setTimeout(() => setSessionMessage(''), 4000);
    }
  };

  const loadTrackedFilters = async () => {
    try {
      setTrackedLoading(true);
      const { data } = await apiService.getTrackedFilters();
      setTrackedFilters(data?.items || []);
    } catch (e) {
      // Silent for users without feature enabled on backend
      console.error('tracked filters load failed', e);
    } finally {
      setTrackedLoading(false);
    }
  };

  const toggleTrackedFilter = async (f) => {
    try {
      setTrackedBusyId(f.id);
      await apiService.updateTrackedFilter(f.id, { is_active: !f.is_active });
      await loadTrackedFilters();
    } catch (e) {
      setTrackedMsg('Failed to update filter');
    } finally {
      setTrackedBusyId(null);
    }
  };

  const deleteTrackedFilter = async (f) => {
    if (!window.confirm(`Delete filter "${f.name}"?`)) return;
    try {
      setTrackedBusyId(f.id);
      await apiService.deleteTrackedFilter(f.id);
      await loadTrackedFilters();
    } catch (e) {
      setTrackedMsg('Failed to delete filter');
    } finally {
      setTrackedBusyId(null);
    }
  };

  const sendTrackedTestEmail = async (f) => {
    try {
      setTrackedBusyId(f.id);
      const { data } = await apiService.sendTrackedFilterTestEmail(f.id);
      if (data?.sent) setTrackedMsg(`Test email sent for "${f.name}"`);
      else setTrackedMsg(data?.error || 'Test email failed');
    } catch (e) {
      setTrackedMsg('Test email failed');
    } finally {
      setTrackedBusyId(null);
      setTimeout(() => setTrackedMsg(''), 5000);
    }
  };

  const openTrackedHistory = async (f) => {
    try {
      setHistoryOpen(true);
      setHistoryFilter(f);
      setHistoryLoading(true);
      const { data } = await apiService.getTrackedFilterHistory(f.id, 60);
      setHistoryRows(data?.items || []);
    } catch (e) {
      setTrackedMsg('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const saveGeos = async () => {
    setSaving(true);
    try {
      await apiService.updateMyGeos(geos);
      updateUser({ allowed_geos: geos });
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const saveApiKeys = async () => {
    setSavingKeys(true);
    setKeyMessage('');
    try {
      const payload = {};
      if (apiKeys.dataforseo_login) payload.dataforseo_login = apiKeys.dataforseo_login;
      if (apiKeys.dataforseo_password) payload.dataforseo_password = apiKeys.dataforseo_password;
      if (apiKeys.ahrefs) payload.ahrefs = apiKeys.ahrefs;
      if (apiKeys.seranking) payload.seranking = apiKeys.seranking;
      if (apiKeys.searchapi) payload.searchapi = apiKeys.searchapi;
      if (apiKeys.openai) payload.openai = apiKeys.openai;
      await apiService.updateMyApiKeys(payload);
      setKeyMessage('API keys saved');
      setApiKeys({ dataforseo_login: '', dataforseo_password: '', ahrefs: '', seranking: '', searchapi: '', openai: '' });
      // Refresh
      const { data } = await apiService.getMyApiKeys();
      setMaskedKeys(data);
      const budgetRes = await apiService.getMyBudget();
      setBudget(budgetRes.data);
    } catch (e) {
      setKeyMessage('Error saving keys');
    }
    setSavingKeys(false);
  };

  const handleDeleteAccount = async () => {
    try {
      await apiService.deleteMyAccount();
      logout();
    } catch (e) { console.error(e); }
  };

  const [revealedKeys, setRevealedKeys] = useState({});

  const handleReveal = async (field) => {
    if (showPassword[field]) {
      // Currently revealed → hide
      setShowPassword(prev => ({ ...prev, [field]: false }));
      setRevealedKeys(prev => ({ ...prev, [field]: '' }));
      return;
    }
    // Reveal: fetch actual value from server
    try {
      const { data } = await apiService.revealApiKey(field);
      setRevealedKeys(prev => ({ ...prev, [field]: data.value || '' }));
      setShowPassword(prev => ({ ...prev, [field]: true }));
    } catch {
      setRevealedKeys(prev => ({ ...prev, [field]: '(error)' }));
      setShowPassword(prev => ({ ...prev, [field]: true }));
    }
  };

  const KeyInput = ({ label, field, isSecret = true, masked }) => {
    const hasNewValue = !!apiKeys[field];
    const isRevealed = showPassword[field] && revealedKeys[field];
    const hasSaved = masked && masked !== 'Not configured' && masked !== 'NOT SET';

    // Determine display value and type
    let displayValue = apiKeys[field]; // user-typed new value
    let inputType = 'text';
    if (hasNewValue && isSecret && !showPassword[field]) {
      inputType = 'password'; // hide typed value
    }

    return (
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
        <div className="flex gap-2">
          {isRevealed && !hasNewValue ? (
            <div className="flex-1 px-3 py-1.5 bg-background border border-primary/50 rounded text-sm text-foreground font-mono select-all">
              {revealedKeys[field]}
            </div>
          ) : (
            <input
              type={inputType}
              value={displayValue}
              onChange={(e) => setApiKeys(prev => ({ ...prev, [field]: e.target.value }))}
              placeholder={masked || 'Not configured'}
              className="flex-1 px-3 py-1.5 bg-background border border-border rounded text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          )}
          {isSecret && hasSaved && !hasNewValue && (
            <button onClick={() => handleReveal(field)}
              className="px-2 text-muted-foreground hover:text-foreground transition-colors" type="button"
              title={showPassword[field] ? 'Hide key' : 'Show key'}>
              {showPassword[field] ? '' : ''}
            </button>
          )}
          {isSecret && hasNewValue && (
            <button onClick={() => setShowPassword(prev => ({ ...prev, [field]: !prev[field] }))}
              className="px-2 text-muted-foreground hover:text-foreground" type="button"
              title={showPassword[field] ? 'Hide' : 'Show'}>
              {showPassword[field] ? '' : ''}
            </button>
          )}
          {isSecret && !hasSaved && !hasNewValue && (
            <span className="px-2 text-muted-foreground/30 flex items-center"></span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Your Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <User className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="text-foreground font-medium">{user?.name || 'User'}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">Role: {user?.role} · Status: {user?.status}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {user?.role !== 'admin' && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span></span>
              Devices & Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-muted-foreground">
                Active devices: <span className="text-foreground font-medium">{sessionCap.count}</span> / {sessionCap.max_sessions}
                {' '}· You can connect <span className="text-foreground font-medium">{sessionCap.remaining_slots}</span> more device(s)
              </div>
              <Button size="sm" variant="outline" onClick={loadMySessions} disabled={sessionLoading}>
                {sessionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Refresh
              </Button>
            </div>
            {sessionMessage && (
              <div className={`mb-3 text-xs ${sessionMessage.toLowerCase().includes('failed') ? 'text-destructive' : 'text-emerald-700'}`}>
                {sessionMessage}
              </div>
            )}
            <div className="border border-border rounded-lg divide-y divide-border/70">
              {sessionLoading ? (
                <div className="px-3 py-4 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading devices...
                </div>
              ) : sessionItems.length === 0 ? (
                <div className="px-3 py-4 text-sm text-muted-foreground">No active devices found.</div>
              ) : sessionItems.map((s) => (
                <div key={s.session_id} className="px-3 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-foreground truncate">
                      {s.is_current ? 'This device' : 'Connected device'}
                      {s.ip ? ` · IP ${s.ip}` : ''}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-medium truncate" title={s.user_agent || ''}>
                      {s.user_agent || 'Unknown user-agent'}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-medium">
                      Last seen: {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString() : '—'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!s.is_current || sessionAction === s.session_id}
                    onClick={() => revokeMySession(s.session_id)}
                    className={s.is_current ? 'opacity-60' : 'text-destructive hover:text-destructive'}
                    title={s.is_current ? 'Current device cannot be removed here' : 'Disconnect this device'}
                  >
                    {sessionAction === s.session_id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                    {s.is_current ? 'Current' : 'Disconnect'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Keys */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
             API Keys
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <KeyInput label="DataForSEO Login" field="dataforseo_login" isSecret={false} masked={maskedKeys.dataforseo_login} />
            <KeyInput label="DataForSEO Password" field="dataforseo_password" masked={maskedKeys.dataforseo_password_set ? 'Password saved' : ''} />
            <KeyInput label="Ahrefs API Key" field="ahrefs" masked={maskedKeys.ahrefs} />
            <KeyInput label="SE Ranking API Key" field="seranking" masked={maskedKeys.seranking} />
            <KeyInput label="SearchAPI.io Key" field="searchapi" masked={maskedKeys.searchapi} />
            <KeyInput label="OpenAI API Key" field="openai" masked={maskedKeys.openai} />
          </div>
          <div className="flex items-center justify-between">
            {keyMessage && <span className={`text-sm ${keyMessage.includes('Error') ? 'text-destructive' : 'text-green-700'}`}>{keyMessage}</span>}
            <Button size="sm" onClick={saveApiKeys} disabled={savingKeys} className="ml-auto">
              {savingKeys ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              <span className="ml-1">Save Keys</span>
            </Button>
          </div>

          {/* Budget summary */}
          {budget?.services && budget.services.length > 0 && (
            <div className="border-t border-border pt-3 mt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">API BUDGET</p>
              <div className="space-y-2">
                {budget.services.map(s => (
                  <div key={s.name} className="flex items-center justify-between p-2 bg-background rounded border border-border">
                    <span className="text-sm text-foreground">{s.name}</span>
                    {!s.configured ? (
                      <span className="text-xs text-muted-foreground">Not configured</span>
                    ) : s.error ? (
                      <span className="text-xs text-red-700">{s.error}</span>
                    ) : s.balance !== undefined ? (
                      <span className="text-sm font-medium text-green-700">${s.balance?.toFixed(2)}</span>
                    ) : s.remaining !== undefined ? (
                      <div className="text-right">
                        <span className="text-sm font-medium text-foreground">{s.remaining?.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground ml-1">/ {s.limit?.toLocaleString()} units</span>
                        <div className="w-24 h-1.5 bg-muted rounded-full mt-1">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(s.percentage_used || 0, 100)}%` }} />
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">{s.note || 'Connected'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {user?.role !== 'admin' && (
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              My GEOs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Select the geographic regions you want to track (max 20):
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(allGeos.length > 0 ? allGeos : ['BR','CA','DE','ES','FR','GB','ID','IN','IT','PL','TH','VN','AU','NL','PT','SE','NO','DK','FI','AT']).map(g => (
                <button key={g} onClick={() => {
                  setGeos(prev => prev.includes(g) ? prev.filter(x => x !== g) : prev.length < 20 ? [...prev, g] : prev);
                }}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    geos.includes(g)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                  }`}>
                  {g}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{geos.length}/20 selected</span>
              <Button size="sm" onClick={saveGeos} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span className="ml-1">Save GEOs</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span></span> Tracked Filters / Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-muted-foreground">
              Create filters from Dashboard via <span className="text-foreground">Track this filter</span>.
            </p>
            <Button size="sm" variant="ghost" onClick={loadTrackedFilters} disabled={trackedLoading}>
              {trackedLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Refresh
            </Button>
          </div>
          {trackedMsg && (
            <div className="text-xs text-emerald-700 mb-2">{trackedMsg}</div>
          )}
          {trackedFilters.length === 0 ? (
            <div className="text-sm text-muted-foreground">No tracked filters yet.</div>
          ) : (
            <div className="space-y-2">
              {trackedFilters.map((f) => (
                <div key={f.id} className="p-3 rounded border border-border bg-background">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-foreground font-medium truncate">{f.name}</div>
                      <div className="text-[11px] text-muted-foreground font-medium">
                        {(f.regions || []).length ? `Regions: ${(f.regions || []).join(', ')}` : 'Regions: all'} ·
                        {' '}Daily: {f.send_daily ? 'on' : 'off'} ·
                        {' '}Last sent: {f.last_sent_at ? new Date(f.last_sent_at).toLocaleString() : '—'} ·
                        {' '}New last run: {f.last_new_brand_count ?? 0}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleTrackedFilter(f)}
                        disabled={trackedBusyId === f.id}
                      >
                        {f.is_active ? 'Pause' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openTrackedHistory(f)}
                        disabled={trackedBusyId === f.id}
                      >
                        History
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendTrackedTestEmail(f)}
                        disabled={trackedBusyId === f.id}
                      >
                        Test email
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive hover:text-destructive"
                        onClick={() => deleteTrackedFilter(f)}
                        disabled={trackedBusyId === f.id}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-foreground">
                History — {historyFilter?.name || 'Tracked filter'}
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setHistoryOpen(false)}>Close</Button>
            </div>
            {historyLoading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading history...
              </div>
            ) : historyRows.length === 0 ? (
              <div className="text-sm text-muted-foreground">No runs yet.</div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto border border-border rounded-lg">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] text-muted-foreground font-medium bg-muted/30">
                  <div className="col-span-2">Run date</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2 text-right">New brands</div>
                  <div className="col-span-3">Sent at</div>
                  <div className="col-span-3">Error</div>
                </div>
                {historyRows.map((r, idx) => (
                  <div key={`${r.run_date}-${idx}`} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-border/60 items-center">
                    <div className="col-span-2 text-foreground">{r.run_date || '—'}</div>
                    <div className="col-span-2">
                      <span className={`px-1.5 py-0.5 rounded text-[11px] ${
                        r.email_status === 'sent'
                          ? 'bg-emerald-50 text-emerald-700'
                          : r.email_status?.includes('failed')
                            ? 'bg-red-50 text-red-700'
                            : 'bg-muted text-muted-foreground'
                      }`}>
                        {r.email_status || '—'}
                      </span>
                    </div>
                    <div className="col-span-2 text-right text-foreground">{r.new_brand_count ?? 0}</div>
                    <div className="col-span-3 text-muted-foreground">
                      {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                    </div>
                    <div className="col-span-3 text-muted-foreground truncate" title={r.error || ''}>
                      {r.error || '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogOut className="w-5 h-5 text-muted-foreground" />
            Account Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" onClick={logout} className="w-full">
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
          {user?.role !== 'admin' && (
            confirmDelete ? (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-sm text-destructive mb-2">Are you sure? This will delete your account and all Cabinet data.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" onClick={handleDeleteAccount}>
                    Yes, delete my account
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setConfirmDelete(true)}
                className="w-full text-destructive hover:text-destructive border-destructive/30">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};
